/**
 * SignInMenu — top-right "Sign in" link with a sign-in-destinations dropdown.
 *
 * The trigger is styled identically to every other top-level nav link
 * (Services, Industries, Portfolio, Tools, Team, Customer Portal, Contact)
 * so the whole bar reads as one consistent menu — no special-case button.
 *
 * The "Customer Portal" entry inside this dropdown takes existing clients
 * STRAIGHT to the salesflow sign-in screen (it is NOT the marketing page
 * — that lives as its own top-level "Customer Portal" nav link, on
 * purpose, because the portal is a competitive differentiator we want
 * visible).
 *
 *   For clients          → Customer Portal      (salesflow.hidroverse.co.za)
 *   HidroVerse admin     ─ HidroVerse Sales Flow (salesflow.hidroverse.co.za)
 *                        ─ Admin sign-in        (/admin/login)
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, ExternalLink, ShieldCheck, Briefcase, User,
} from "lucide-react";

const SALESFLOW_URL = "https://salesflow.hidroverse.co.za";

export function SignInMenu({ dark = true, mobile = false }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const ref = useRef(null);

  const openNow = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 140);
  };

  useEffect(() => {
    if (!open) return;
    const handleDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const linkColor = dark ? "text-white/85" : "text-[#0e0f10]";

  // ─── Mobile — renders inside the burger menu as sub-items, matching
  // the look of the Tools / Industries collapsible sub-lists.
  if (mobile) {
    const txt = dark ? "text-white/85" : "text-[#0e0f10]";
    const itemStyle = { fontSize: "0.82rem", opacity: 0.78 };
    return (
      <div className="flex flex-col gap-2" data-testid="nav-signin-mobile">
        <a href={SALESFLOW_URL} target="_blank" rel="noreferrer"
           className={`${txt} font-light`} style={itemStyle}
           data-testid="nav-mobile-signin-customer">
          Customer Portal sign-in
        </a>
        <a href={SALESFLOW_URL} target="_blank" rel="noreferrer"
           className={`${txt} font-light`} style={itemStyle}
           data-testid="nav-mobile-signin-salesflow">
          HidroVerse Sales Flow
        </a>
        <Link to="/admin/login"
              className={`${txt} font-light`} style={itemStyle}
              data-testid="nav-mobile-signin-admin">
          Admin sign-in
        </Link>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="nav-signin-button"
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${linkColor} hv-link text-[0.74rem] tracking-[0.22em] uppercase font-semibold inline-flex items-center gap-1`}
        style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
      >
        Sign in <ChevronDown size={13} />
      </button>

      {open && (
        <div
          className="absolute top-full right-0 pt-4 w-[360px] z-50"
          data-testid="nav-signin-menu"
        >
          <div
            className="overflow-hidden"
            style={{
              background: "rgba(255,255,255,0.92)",
              backdropFilter: "blur(24px) saturate(140%)",
              WebkitBackdropFilter: "blur(24px) saturate(140%)",
              border: "1px solid rgba(12,42,64,0.08)",
              borderRadius: 14,
              boxShadow:
                "0 1px 2px rgba(12,42,64,0.04), 0 36px 72px -16px rgba(12,42,64,0.28)",
            }}
          >
            {/* Charcoal-glass header bar (same as Tools / Industries) */}
            <div
              className="px-5 py-3.5 flex items-center justify-between"
              style={{
                background: "rgba(22,24,27,0.88)",
                backdropFilter: "blur(20px) saturate(160%)",
                WebkitBackdropFilter: "blur(20px) saturate(160%)",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                color: "#F5F7FA",
              }}
            >
              <div>
                <div
                  className="font-mono uppercase mb-0.5"
                  style={{
                    fontSize: "0.6rem",
                    letterSpacing: "0.28em",
                    color: "#1FB6C1",
                    fontWeight: 700,
                  }}
                >
                  Sign in
                </div>
                <div
                  className="font-display"
                  style={{ fontSize: "0.88rem", fontWeight: 500,
                           color: "rgba(245,247,250,0.95)" }}
                >
                  Pick your door.
                </div>
              </div>
            </div>

            <div style={{ background: "rgba(12,42,64,0.06)" }}>
              <SectionLabel>For clients</SectionLabel>
              <Item
                href={SALESFLOW_URL}
                external
                testid="nav-signin-customer"
                icon={<User size={16} strokeWidth={1.75} />}
                title="Customer Portal"
                sub="Sign in to your account"
                onClick={() => setOpen(false)}
              />

              <SectionLabel>HidroVerse admin</SectionLabel>
              <Item
                href={SALESFLOW_URL}
                external
                testid="nav-signin-salesflow"
                icon={<Briefcase size={16} strokeWidth={1.75} />}
                title="HidroVerse Sales Flow"
                sub="Quotes · jobs · CRM"
                onClick={() => setOpen(false)}
              />
              <Item
                to="/admin/login"
                testid="nav-signin-admin"
                icon={<ShieldCheck size={16} strokeWidth={1.75} />}
                title="Admin sign-in"
                sub="Website admin tools"
                onClick={() => setOpen(false)}
                last
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Subcomponents ──────────────────────────────────────────────────── */

function SectionLabel({ children }) {
  return (
    <div
      className="font-mono uppercase"
      style={{
        fontSize: "0.58rem",
        letterSpacing: "0.22em",
        fontWeight: 700,
        color: "rgba(12,42,64,0.55)",
        background: "#FFFFFF",
        padding: "10px 18px 4px",
        borderBottom: "1px solid rgba(12,42,64,0.04)",
      }}
    >
      {children}
    </div>
  );
}

function Item({ to, href, external, icon, title, sub, testid, onClick, last }) {
  const inner = (
    <div
      className="flex items-center gap-3 px-4 py-3 transition-colors duration-200 group"
      style={{
        background: "#FFFFFF",
        borderBottom: last ? "none" : "1px solid rgba(12,42,64,0.04)",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAF7")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "#FFFFFF")}
    >
      <span
        className="inline-flex items-center justify-center shrink-0"
        style={{
          width: 32,
          height: 32,
          borderRadius: 6,
          background: "rgba(31,182,193,0.10)",
          color: "#0C2A40",
        }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="font-display leading-tight"
          style={{
            fontSize: "0.88rem",
            color: "#0C2A40",
            fontWeight: 600,
            letterSpacing: "-0.005em",
          }}
        >
          {title}
        </div>
        {sub && (
          <div
            className="mt-0.5 font-light leading-snug"
            style={{ fontSize: "0.74rem", color: "rgba(26,31,37,0.7)" }}
          >
            {sub}
          </div>
        )}
      </div>
      {external && (
        <ExternalLink size={12} style={{ color: "rgba(12,42,64,0.40)" }} />
      )}
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        data-testid={testid}
        onClick={onClick}
        style={{ textDecoration: "none", color: "inherit", display: "block" }}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onClick}
      style={{ textDecoration: "none", color: "inherit", display: "block" }}
    >
      {inner}
    </Link>
  );
}
